/**
 * OrganizationService
 * Verwaltet Organisationen und die aktuelle Organisation-Auswahl
 */

class OrganizationService {
    constructor(supabase) {
        this.supabase = supabase;
        this.currentOrganization = null;
        this.organizations = [];
        this.listeners = [];
        this.isInitialized = false; // Verhindert mehrfache Initialisierung
    }

    /**
     * Lädt alle Organisationen des aktuellen Users
     * @returns {Promise<Array>} Liste der Organisationen
     */
    async loadUserOrganizations() {
        try {
            // Hole User-Session
            const { data: { session }, error: authError } = await this.supabase.auth.getSession();
            if (authError || !session) {
                throw new Error('Nicht eingeloggt');
            }

            // Hole User-Organisationen
            const { data: userOrgs, error: userOrgError } = await this.supabase
                .from('new_user_organizations')
                .select('organization_id, role, created_at')
                .eq('user_id', session.user.id)
                .order('created_at', { ascending: false });

            if (userOrgError) {
                console.error('❌ Fehler beim Laden der User-Organisationen:', userOrgError);
                throw userOrgError;
            }

            if (!userOrgs || userOrgs.length === 0) {
                this.organizations = [];
                return [];
            }

            // Hole Organisations-Daten separat
            const orgIds = userOrgs.map(uo => uo.organization_id);
            const { data: orgs, error: orgError } = await this.supabase
                .from('new_organizations')
                .select('id, name, slug, created_at, plan, logo_url')
                .in('id', orgIds);

            if (orgError) {
                console.error('❌ Fehler beim Laden der Organisationen:', orgError);
                throw orgError;
            }

            // Kombiniere die Daten
            this.organizations = userOrgs
                .map(userOrg => {
                    const org = orgs.find(o => o.id === userOrg.organization_id);
                    if (!org) {
                        console.warn('⚠️ Organisation nicht gefunden:', userOrg.organization_id);
                        return null;
                    }
                    return {
                        id: org.id,
                        name: org.name,
                        slug: org.slug,
                        role: userOrg.role,
                        membershipCreatedAt: userOrg.created_at || null,
                        createdAt: org.created_at,
                        plan: org.plan || 'free',
                        logoUrl: org.logo_url || null
                    };
                })
                .filter(org => org !== null); // Filtere nicht gefundene Orgs

            // Setze erste Organisation als aktiv, falls noch keine gesetzt
            if (!this.currentOrganization && this.organizations.length > 0) {
                this.setCurrentOrganization(this.organizations[0].id);
            }

            return this.organizations;

        } catch (error) {
            console.error('❌ Fehler beim Laden der Organisationen:', error);
            throw error;
        }
    }

    /**
     * Setzt die aktuelle Organisation
     * @param {string} orgId - Organisation-ID
     */
    setCurrentOrganization(orgId) {
        const org = this.organizations.find(o => o.id === orgId);
        if (!org) {
            throw new Error('Organisation nicht gefunden');
        }

        // Prüfe ob die Organisation bereits gesetzt ist
        if (this.currentOrganization && this.currentOrganization.id === orgId) {
            // Stille Logs - Organisation bereits gesetzt
            return;
        }

        this.currentOrganization = org;
        
        // Speichere in localStorage
        localStorage.setItem('visualize_current_org', orgId);

        // Benachrichtige Listener
        this.notifyListeners();
    }

    /**
     * Gibt die aktuelle Organisation zurück
     * @returns {Object|null} Aktuelle Organisation
     */
    getCurrentOrganization() {
        return this.currentOrganization;
    }

    /**
     * Gibt die ID der aktuellen Organisation zurück
     * @returns {string|null} Organisation-ID
     */
    getCurrentOrganizationId() {
        return this.currentOrganization ? this.currentOrganization.id : null;
    }

    /**
     * Gibt den Feature-Plan der aktuellen Organisation zurück
     * @returns {'free'|'paid'} Plan (default: 'free')
     */
    getCurrentOrganizationPlan() {
        return (this.currentOrganization && this.currentOrganization.plan) || 'free';
    }

    /**
     * Prüft ob die aktuelle Organisation einen Paid-Plan hat
     * @returns {boolean}
     */
    isPaidPlan() {
        return this.getCurrentOrganizationPlan() === 'paid';
    }

    /**
     * Prüft ob User Admin der aktuellen Organisation ist
     * @returns {boolean} True wenn Admin
     */
    isCurrentOrgAdmin() {
        return this.currentOrganization && this.currentOrganization.role === 'admin';
    }

    /**
     * Gibt die Rolle des Users in der aktuellen Organisation zurück
     * @returns {string|null} z.B. 'admin', 'owner', 'editor', 'member', 'viewer'
     */
    getCurrentOrganizationRole() {
        if (!this.currentOrganization || !this.currentOrganization.role) {
            return null;
        }
        return String(this.currentOrganization.role).toLowerCase();
    }

    /**
     * Prüft ob User in der aktuellen Organisation Viewer ist
     * @returns {boolean}
     */
    isCurrentOrgViewer() {
        return this.getCurrentOrganizationRole() === 'viewer';
    }

    /**
     * Gibt alle Organisationen zurück
     * @returns {Array} Liste der Organisationen
     */
    getOrganizations() {
        return this.organizations;
    }

    /**
     * Erstellt eine neue Organisation
     * @param {string} name - Name der Organisation
     * @returns {Promise<Object>} Neue Organisation
     */
    async createOrganization(name) {
        try {
            const trimmedName = (name || '').trim();
            if (!trimmedName) {
                throw new Error('Organisationsname darf nicht leer sein');
            }

            const { data: { session }, error: authError } = await this.supabase.auth.getSession();
            if (authError || !session) {
                throw new Error('Nicht eingeloggt');
            }

            // Sichere RPC: erstellt Organisation + initiale Admin-Membership atomar
            const { data: rpcOrg, error: rpcError } = await this.supabase.rpc('create_organization_with_admin', {
                p_name: trimmedName
            });

            let org = rpcOrg;
            if (rpcError) {
                const rpcErrorMessage = String(rpcError.message || '');
                const isMissingRpc = rpcError.code === 'PGRST202'
                    || rpcError.code === '42883'
                    || rpcErrorMessage.includes('create_organization_with_admin');

                // Rollout-Kompatibilität: Fallback für Umgebungen ohne neue RPC
                if (!isMissingRpc) {
                    throw rpcError;
                }

                const legacySlug = trimmedName
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '')
                    .substring(0, 100);

                const { data: legacyOrg, error: legacyOrgError } = await this.supabase
                    .from('new_organizations')
                    .insert({
                        name: trimmedName,
                        slug: legacySlug || 'org-' + Date.now()
                    })
                    .select()
                    .single();

                if (legacyOrgError) throw legacyOrgError;

                const { error: userOrgError } = await this.supabase
                    .from('new_user_organizations')
                    .insert({
                        user_id: session.user.id,
                        organization_id: legacyOrg.id,
                        role: 'admin'
                    });

                if (userOrgError) throw userOrgError;
                org = legacyOrg;
            }

            if (!org || !org.id) {
                throw new Error('Organisation konnte nicht erstellt werden');
            }

            // Lade Organisationen neu
            await this.loadUserOrganizations();

            return org;

        } catch (error) {
            console.error('❌ Fehler beim Erstellen der Organisation:', error);
            throw error;
        }
    }

    /**
     * Umbenennen einer Organisation (nur Admin)
     * @param {string} orgId - Organisation-ID
     * @param {string} newName - Neuer Name
     * @returns {Promise<Object>} Aktualisierte Organisation
     */
    async renameOrganization(orgId, newName) {
        try {
            // Prüfe ob User Admin ist
            const org = this.organizations.find(o => o.id === orgId);
            if (!org) {
                throw new Error('Organisation nicht gefunden');
            }
            if (org.role !== 'admin') {
                throw new Error('Nur Admins können Organisationen umbenennen');
            }

            // Generiere neuen slug
            const slug = newName
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '')
                .substring(0, 100);

            // Update Organisation
            const { data, error } = await this.supabase
                .from('new_organizations')
                .update({ 
                    name: newName,
                    slug: slug || 'org-' + Date.now()
                })
                .eq('id', orgId)
                .select()
                .single();

            if (error) throw error;

            // Aktualisiere lokale Daten
            await this.loadUserOrganizations();

            return data;

        } catch (error) {
            console.error('❌ Fehler beim Umbenennen:', error);
            throw error;
        }
    }

    /**
     * Speichert die Logo-URL einer Organisation (nur Admin).
     * @param {string} orgId - Organisation-ID
     * @param {string|null} logoUrl - Öffentliche URL des Logos oder null zum Entfernen
     * @returns {Promise<Object>} Aktualisierte Organisation
     */
    async updateOrganizationLogo(orgId, logoUrl) {
        try {
            const org = this.organizations.find(o => o.id === orgId);
            if (!org) {
                throw new Error('Organisation nicht gefunden');
            }
            if (org.role !== 'admin') {
                throw new Error('Nur Admins können das Logo ändern');
            }

            const { data, error } = await this.supabase
                .from('new_organizations')
                .update({ logo_url: logoUrl })
                .eq('id', orgId)
                .select()
                .single();

            if (error) throw error;

            await this.loadUserOrganizations();
            return data;

        } catch (error) {
            console.error('❌ Fehler beim Speichern des Logos:', error);
            throw error;
        }
    }

    /**
     * Setzt den Feature-Plan der Organisation (nur Admin)
     * @param {string} orgId - Organisation-ID
     * @param {'free'|'paid'} plan - Plan
     * @returns {Promise<Object>} Aktualisierte Organisation
     */
    async setOrganizationPlan(orgId, plan) {
        try {
            if (plan !== 'free' && plan !== 'paid') {
                throw new Error('Plan muss "free" oder "paid" sein');
            }
            const org = this.organizations.find(o => o.id === orgId);
            if (!org) {
                throw new Error('Organisation nicht gefunden');
            }
            if (org.role !== 'admin') {
                throw new Error('Nur Admins können den Plan ändern');
            }

            const { data, error } = await this.supabase
                .from('new_organizations')
                .update({ plan })
                .eq('id', orgId)
                .select()
                .single();

            if (error) throw error;

            await this.loadUserOrganizations();
            if (this.currentOrganization && this.currentOrganization.id === orgId) {
                this.currentOrganization = this.organizations.find(o => o.id === orgId) || this.currentOrganization;
                this.notifyListeners();
            }
            return data;
        } catch (error) {
            console.error('❌ Fehler beim Setzen des Plans:', error);
            throw error;
        }
    }

    async listOrganizationInvites(orgId = null) {
        try {
            const targetOrgId = orgId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const { data, error } = await this.supabase.rpc('list_organization_invites', {
                p_organization_id: targetOrgId
            });

            if (error) throw error;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('❌ Fehler beim Laden der Einladungen:', error);
            throw error;
        }
    }

    async createOrganizationInvite({
        organizationId = null,
        email,
        role = 'viewer',
        expiresInHours = 72
    }) {
        try {
            const targetOrgId = organizationId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const trimmedEmail = String(email || '').trim().toLowerCase();
            if (!trimmedEmail) {
                throw new Error('E-Mail ist erforderlich');
            }

            const normalizedRole = String(role || '').trim().toLowerCase();
            if (!['viewer', 'editor', 'admin'].includes(normalizedRole)) {
                throw new Error('Ungültige Rolle (erlaubt: viewer, editor, admin)');
            }

            const currentOrg = this.organizations.find((org) => org.id === targetOrgId);
            const roleName = currentOrg?.role || this.currentOrganization?.role || null;
            if (roleName !== 'admin' && roleName !== 'owner') {
                throw new Error('Nur Admins duerfen Einladungen erstellen');
            }

            const { data, error } = await this.supabase.rpc('create_organization_invite', {
                p_organization_id: targetOrgId,
                p_email: trimmedEmail,
                p_role: normalizedRole,
                p_expires_in_hours: expiresInHours
            });

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('❌ Fehler beim Erstellen der Einladung:', error);
            throw error;
        }
    }

    async revokeOrganizationInvite(inviteId) {
        try {
            if (!inviteId) {
                throw new Error('inviteId ist erforderlich');
            }

            const { data, error } = await this.supabase.rpc('revoke_organization_invite', {
                p_invite_id: inviteId
            });

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('❌ Fehler beim Zurückziehen der Einladung:', error);
            throw error;
        }
    }

    async deleteOrganizationInvite(inviteId) {
        try {
            if (!inviteId) {
                throw new Error('inviteId ist erforderlich');
            }

            const { data, error } = await this.supabase.rpc('delete_organization_invite', {
                p_invite_id: inviteId
            });

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('❌ Fehler beim Löschen der Einladung:', error);
            throw error;
        }
    }

    async listOrganizationMembers(orgId = null) {
        try {
            const targetOrgId = orgId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const { data, error } = await this.supabase.rpc('list_organization_members', {
                p_organization_id: targetOrgId
            });

            if (error) throw error;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('❌ Fehler beim Laden der Organisationsmitglieder:', error);
            throw error;
        }
    }

    async updateOrganizationMemberRole({
        organizationId = null,
        userId,
        role
    }) {
        try {
            const targetOrgId = organizationId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const targetUserId = String(userId || '').trim();
            if (!targetUserId) {
                throw new Error('userId ist erforderlich');
            }

            const normalizedRole = String(role || '').trim().toLowerCase();
            if (!['viewer', 'member', 'editor', 'admin'].includes(normalizedRole)) {
                throw new Error('Ungültige Rolle (erlaubt: viewer, member, editor, admin)');
            }

            const currentOrg = this.organizations.find((org) => org.id === targetOrgId);
            const actorRole = String(currentOrg?.role || this.currentOrganization?.role || '').toLowerCase();
            if (actorRole !== 'admin' && actorRole !== 'owner') {
                throw new Error('Nur Admins dürfen Rollen ändern');
            }

            const { data, error } = await this.supabase.rpc('update_organization_member_role', {
                p_organization_id: targetOrgId,
                p_target_user_id: targetUserId,
                p_new_role: normalizedRole
            });

            if (error) throw error;

            // Lokale Org-Rollen aktualisieren (wichtig falls eigene Rolle geaendert wurde).
            await this.loadUserOrganizations();
            if (this.currentOrganization && this.currentOrganization.id === targetOrgId) {
                this.currentOrganization = this.organizations.find((org) => org.id === targetOrgId) || this.currentOrganization;
                this.notifyListeners();
            }

            return data;
        } catch (error) {
            console.error('❌ Fehler beim Aktualisieren der Mitgliedsrolle:', error);
            throw error;
        }
    }

    async removeOrganizationMember({
        organizationId = null,
        userId
    }) {
        try {
            const targetOrgId = organizationId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const targetUserId = String(userId || '').trim();
            if (!targetUserId) {
                throw new Error('userId ist erforderlich');
            }

            const currentOrg = this.organizations.find((org) => org.id === targetOrgId);
            const actorRole = String(currentOrg?.role || this.currentOrganization?.role || '').toLowerCase();
            if (actorRole !== 'admin' && actorRole !== 'owner') {
                throw new Error('Nur Admins dürfen Mitglieder entfernen');
            }

            const { data, error } = await this.supabase.rpc('remove_organization_member', {
                p_organization_id: targetOrgId,
                p_target_user_id: targetUserId
            });

            if (error) throw error;

            // Lokale Org-Rollen aktualisieren (wichtig falls eigener Account betroffen ist).
            await this.loadUserOrganizations();
            if (this.currentOrganization && this.currentOrganization.id === targetOrgId) {
                this.currentOrganization = this.organizations.find((org) => org.id === targetOrgId) || this.currentOrganization;
                this.notifyListeners();
            }

            return data;
        } catch (error) {
            console.error('❌ Fehler beim Entfernen des Mitglieds:', error);
            throw error;
        }
    }

    async transferOrganizationOwner({
        organizationId = null,
        userId
    }) {
        try {
            const targetOrgId = organizationId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const targetUserId = String(userId || '').trim();
            if (!targetUserId) {
                throw new Error('userId ist erforderlich');
            }

            const currentOrg = this.organizations.find((org) => org.id === targetOrgId);
            const actorRole = String(currentOrg?.role || this.currentOrganization?.role || '').toLowerCase();
            if (actorRole !== 'owner') {
                throw new Error('Nur Owner darf die Inhaberschaft übertragen');
            }

            const { data, error } = await this.supabase.rpc('transfer_organization_owner', {
                p_organization_id: targetOrgId,
                p_new_owner_user_id: targetUserId
            });

            if (error) throw error;

            await this.loadUserOrganizations();
            if (this.currentOrganization && this.currentOrganization.id === targetOrgId) {
                this.currentOrganization = this.organizations.find((org) => org.id === targetOrgId) || this.currentOrganization;
                this.notifyListeners();
            }

            return data;
        } catch (error) {
            console.error('❌ Fehler beim Übertragen der Inhaberschaft:', error);
            throw error;
        }
    }

    /**
     * Löscht die Organisation inkl. aller Mitgliedschaften.
     * Erlaubt auch, wenn der aktuelle User der einzige Admin/Owner ist (Org wird aufgelöst).
     * @param {string} [organizationId] - Org-ID (default: aktuelle Org)
     * @returns {Promise<{ status: string, organization_id: string, members_deleted: number }>}
     */
    async deleteOrganization(organizationId = null) {
        try {
            const targetOrgId = organizationId || this.getCurrentOrganizationId();
            if (!targetOrgId) {
                throw new Error('Keine Organisation ausgewählt');
            }

            const currentOrg = this.organizations.find((org) => org.id === targetOrgId);
            const actorRole = String(currentOrg?.role || this.currentOrganization?.role || '').toLowerCase();
            if (actorRole !== 'admin' && actorRole !== 'owner') {
                throw new Error('Nur Admin oder Owner können die Organisation löschen');
            }

            const { data, error } = await this.supabase.rpc('delete_organization', {
                p_organization_id: targetOrgId
            });

            if (error) throw error;

            await this.loadUserOrganizations();
            this.notifyListeners();
            return data;
        } catch (error) {
            console.error('❌ Fehler beim Löschen der Organisation:', error);
            throw error;
        }
    }

    async acceptOrganizationInvite(token, firstName = null, lastName = null) {
        try {
            const inviteToken = String(token || '').trim();
            if (!inviteToken) {
                throw new Error('invite token ist erforderlich');
            }

            const { data, error } = await this.supabase.rpc('accept_organization_invite', {
                p_token: inviteToken,
                p_first_name: firstName || null,
                p_last_name: lastName || null
            });

            if (error) throw error;

            await this.loadUserOrganizations();
            const acceptedOrganizationId = data?.organization_id || null;
            if (acceptedOrganizationId && this.organizations.some((org) => org.id === acceptedOrganizationId)) {
                this.setCurrentOrganization(acceptedOrganizationId);
            }

            return data;
        } catch (error) {
            console.error('❌ Fehler beim Annehmen der Einladung:', error);
            throw error;
        }
    }

    /**
     * Registriert einen Listener für Organisations-Änderungen
     * @param {Function} callback - Callback-Funktion
     */
    addListener(callback) {
        this.listeners.push(callback);
    }

    /**
     * Entfernt einen Listener
     * @param {Function} callback - Callback-Funktion
     */
    removeListener(callback) {
        this.listeners = this.listeners.filter(l => l !== callback);
    }

    /**
     * Benachrichtigt alle Listener
     */
    notifyListeners() {
        this.listeners.forEach(callback => {
            try {
                callback(this.currentOrganization);
            } catch (error) {
                console.error('❌ Fehler beim Benachrichtigen eines Listeners:', error);
            }
        });
    }

    /**
     * Initialisiert den Service
     * @returns {Promise<void>}
     */
    async init() {
        // Verhindere mehrfache Initialisierung
        if (this.isInitialized) {
            return;
        }

        try {
            // Lade Organisationen
            await this.loadUserOrganizations();

            // Versuche gespeicherte Organisation aus localStorage zu laden
            const savedOrgId = localStorage.getItem('visualize_current_org');
            if (savedOrgId && this.organizations.find(o => o.id === savedOrgId)) {
                this.setCurrentOrganization(savedOrgId);
            }

            this.isInitialized = true;
        } catch (error) {
            console.error('❌ Fehler beim Initialisieren des OrganizationService:', error);
            throw error;
        }
    }
}

export { OrganizationService };
